import { useMemo } from 'react';

import { Grid, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import { AssetIcon } from 'src/v2/components/ui/icons/assets';
import { GenericInfoLabel } from 'src/v2/components/ui/label';
import { LoadSkeleton } from 'src/v2/components/ui/loadSkeleton';

import { useDashboardStats } from '~components/hooks/dashboard/useDashboardStats';
import { useIsMobile } from '~components/hooks/utils/useIsMobile';
import { black, primaryColors } from '~components/theme/styles/colors';
import { defaultBackground } from '~components/theme/styles/mixins';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { getAssetConfig } from '~config/protocol';
import { ENativeAsset, EVestaAsset } from '~config/types';

import { SvgGradient } from './svgGradient';

const MOBILE_INNER_RADIUS = 80;

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, payload, percent } = props;
  const isMobile = innerRadius === MOBILE_INNER_RADIUS;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 50) * cos;
  const my = cy + (outerRadius + 50) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * (isMobile ? 1 : 40);
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={black['45%']}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 20}
        fill={`url(#${payload.name}-gradient)`}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={primaryColors[700]} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={primaryColors[700]} stroke="none" />
      <Typography
        component="text"
        variant={isMobile ? 'label2' : 'label1'}
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill={primaryColors[100]}
      >
        {payload.name}
      </Typography>
      <Typography
        component="text"
        variant={isMobile ? 'label3' : 'label2'}
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={25}
        textAnchor={textAnchor}
        fill={primaryColors[400]}
      >
        {`${(percent * 100).toFixed(2)}%`}
      </Typography>
    </g>
  );
};

export const COLLATERALIZATION_PIE_CHART_HEIGHT = 576;

export const CollateralizationGraph: React.FC = () => {
  const { data: dashboardData, isLoading: isDashboardLoading } = useDashboardStats('Week');

  const isMobile = useIsMobile();

  const vstCollateralization = useMemo(() => {
    const todayStats = dashboardData[dashboardData.length - 1];

    const todayCollateralization =
      todayStats?.stats?.vstMinted?.collaterals ??
      Object.fromEntries(Object.values(EVestaAsset).map(asset => [asset, { vstMinted: '0' }]));

    return Object.entries(todayCollateralization)
      .filter(([_, { vstMinted }]) => vstMinted !== '0')
      .map(([asset, { vstMinted }]) => ({ name: asset, value: Number(vstMinted) })) as {
      value: number;
      name: EVestaAsset;
    }[];
  }, [JSON.stringify(dashboardData)]);

  if (isDashboardLoading) {
    return (
      <LoadSkeleton isLoading={isDashboardLoading} width={1088} height={COLLATERALIZATION_PIE_CHART_HEIGHT} />
    );
  }

  return (
    <Grid
      container
      sx={{
        height: isMobile ? 'fit-content' : `100%`,
        alignItems: 'center',
      }}
    >
      <Grid item xs={12} sm={3} sx={{ paddingTop: 0 }}>
        {vstCollateralization.map(({ name, value }, index) => (
          <Grid
            key={name}
            sx={{
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              marginTop: index === 0 ? undefined : '16px',
              ...defaultBackground,
            }}
          >
            <AssetIcon asset={name} />
            <Grid>
              <GenericInfoLabel labelTitle={`${ENativeAsset.VST} Minted`} />
              <Typography variant="label2">
                {displayFormatUnits({ value, displayType: EDisplayType.USD, decimal: 0 })}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Grid
        item
        xs={12}
        sm={9}
        sx={{
          height: `${COLLATERALIZATION_PIE_CHART_HEIGHT}px`,
          flex: 1,
          '.recharts-responsive-container .recharts-wrapper': {
            position: 'absolute !important',
          },
          paddingTop: 0,
        }}
      >
        <ResponsiveContainer width="99%" height="99%">
          <PieChart>
            <defs>
              {Object.values(EVestaAsset).map(asset => (
                <SvgGradient
                  id={`${asset}-gradient`}
                  key={asset}
                  gradient={getAssetConfig(asset).styles.gradient}
                />
              ))}
            </defs>
            <Pie
              data={vstCollateralization}
              cx={'50%'}
              cy={'50%'}
              paddingAngle={3}
              dataKey="value"
              innerRadius={isMobile ? MOBILE_INNER_RADIUS : 160}
              outerRadius={isMobile ? 100 : 200}
              activeShape={renderActiveShape}
              activeIndex={vstCollateralization.map((_, index) => index)}
              label={false}
            >
              {vstCollateralization.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={black['45%']} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Grid>
    </Grid>
  );
};
